import { zodResolver } from '@hookform/resolvers/zod'
import type { BackofficeAuthSignInWithEmailData } from '@repo/sdk/gen'
import { backofficeAuthSignInWithEmail } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { Checkbox } from '@repo/ui/checkbox'
import { ButtonLink } from '@repo/ui/components/button-link'
import { InputPassword } from '@repo/ui/components/input-password'
import { formatLocaleDate } from '@repo/ui/dates'
import { Field, FieldError, FieldGroup, FieldLabel } from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import i18n, { useTranslation } from '@/lib/i18n'
import { fetchFreshSession } from '@/query/query-options'
import { AuthBrand } from '../-components/auth-brand'

const LoginSchema = z.object({
	email: z.email({ error: () => i18n.t('auth:validation.email_invalid') }),
	password: z
		.string()
		.min(1, { error: () => i18n.t('auth:validation.password_invalid') }),
	rememberMe: z.boolean()
})

export const Route = createFileRoute('/(auth)/login/')({
	validateSearch: z.object({
		redirect: z.string().startsWith('/').optional(),
		error: z.string().optional()
	}),
	component: AdminLogin
})

function AdminLogin() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { redirect, error } = Route.useSearch()

	const form = useForm<z.infer<typeof LoginSchema>>({
		resolver: zodResolver(LoginSchema),
		defaultValues: { email: '', password: '', rememberMe: false }
	})

	const signIn = useMutation({
		mutationFn: (body: BackofficeAuthSignInWithEmailData['body']) =>
			backofficeAuthSignInWithEmail({ body }),
		onError: () => form.resetField('password'),
		onSuccess: async () => {
			// Auth state just changed server-side (the session was issued):
			// revalidate the cached session before navigating so the target
			// route's guard reads the fresh state.
			await fetchFreshSession(queryClient)
			await navigate({ to: redirect ?? '/' })
		}
	})

	return (
		<AuthCard>
			<AuthCardHeader
				description={t('auth:login.description')}
				icon={<AuthBrand />}
				title={t('auth:login.title')}
			/>
			<AuthCardBody>
				{error != null && (
					<AuthErrorAlert
						onClose={() =>
							navigate({
								replace: true,
								search: { redirect },
								to: '/login'
							})
						}
						title={t('auth:login.sso_error')}
					/>
				)}
				<form
					onSubmit={form.handleSubmit((data) => signIn.mutate(data))}
				>
					<FieldGroup>
						{signIn.isError && (
							<AuthErrorAlert
								onClose={() => signIn.reset()}
								title={signInErrorMessage(signIn.error)}
							/>
						)}
						<Controller
							control={form.control}
							name='email'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('common:email')}
									</FieldLabel>
									<Input
										{...field}
										aria-invalid={fieldState.invalid}
										id={field.name}
										placeholder={t(
											'auth:email_placeholder'
										)}
										type='email'
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
						<Controller
							control={form.control}
							name='password'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<div className='flex items-center justify-between'>
										<FieldLabel htmlFor={field.name}>
											{t('common:password')}
										</FieldLabel>
										<ButtonLink
											size='xs'
											to='/forgot-password'
											variant='link'
										>
											{t('auth:login.forgot_password')}
										</ButtonLink>
									</div>
									<InputPassword
										{...field}
										aria-invalid={fieldState.invalid}
										id={field.name}
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
						<Controller
							control={form.control}
							name='rememberMe'
							render={({ field }) => (
								<Field orientation='horizontal'>
									<Checkbox
										checked={field.value}
										id={field.name}
										onCheckedChange={(checked) =>
											field.onChange(checked === true)
										}
									/>
									<FieldLabel
										className='font-normal'
										htmlFor={field.name}
									>
										{t('auth:login.remember')}
									</FieldLabel>
								</Field>
							)}
						/>
						<Field>
							<Button disabled={signIn.isPending} type='submit'>
								{signIn.isPending ? (
									<Spinner />
								) : (
									t('auth:log_in')
								)}
							</Button>
						</Field>
					</FieldGroup>
				</form>
			</AuthCardBody>
		</AuthCard>
	)
}

/** A locked account says until when; every other error has its shared text. */
function signInErrorMessage(error: unknown) {
	if (
		typeof error === 'object' &&
		error !== null &&
		'code' in error &&
		error.code === 'account_locked' &&
		'lockedUntil' in error &&
		typeof error.lockedUntil === 'string'
	) {
		return i18n.t('auth:login.locked_until', {
			time: formatLocaleDate(error.lockedUntil, 'HH:mm')
		})
	}
	return formatError(error)
}
